import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomSelect,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../common';
import {
  buildIssuePayloadFromItemRows,
  formatSupplyOrderQuantity,
  getProviderDisplayLabel,
  getSupplyOrderApiErrorMessage,
} from '../utils/supplyOrdersUtils';

const IssueSupplyOrderDialog = ({
  open,
  onClose,
  order,
  providers = [],
  onIssue,
  isProcessing = false,
}) => {
  const [rows, setRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const orderItems = useMemo(() => order?.items || [], [order?.items]);

  useEffect(() => {
    if (open && order) {
      setRows(
        orderItems.map((item) => ({
          uuid: item.uuid,
          article_label: `${item.article_code_snapshot} — ${item.article_name_snapshot}`,
          requested_quantity: item.requested_quantity,
          uuid_provider_legal_entity: item.uuid_provider_legal_entity || '',
          provider_delivery_commitment_at: item.provider_delivery_commitment_at
            ? item.provider_delivery_commitment_at.split('T')[0]
            : '',
        }))
      );
      setErrorMessage('');
    }
  }, [open, order, orderItems]);

  const handleRowChange = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const handleIssue = async () => {
    const missingProvider = rows.some((row) => !row.uuid_provider_legal_entity);
    if (missingProvider) {
      setErrorMessage('Seleccioná un proveedor para cada artículo.');
      return;
    }

    const payload = buildIssuePayloadFromItemRows(rows);
    if (!payload) {
      setErrorMessage('No hay artículos para emitir.');
      return;
    }

    try {
      setErrorMessage('');
      await onIssue(payload);
      onClose();
    } catch (error) {
      setErrorMessage(
        getSupplyOrderApiErrorMessage(error, 'No se pudo emitir la orden.')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer open={open} onClose={onClose} size="large" minHeight="50vh">
      <DialogTitle>Emitir orden a proveedor</DialogTitle>
      <DialogBody>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <Alert severity="info">
            Asigná proveedor y compromiso por artículo. Si hay distintas combinaciones, se
            generarán varias órdenes emitidas.
          </Alert>

          <Typography variant="subtitle1" fontWeight={700}>
            Artículos
          </Typography>

          <GeneralTableBox>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <HeadTableCell>Artículo</HeadTableCell>
                  <HeadTableCell>Cantidad</HeadTableCell>
                  <HeadTableCell>Proveedor</HeadTableCell>
                  <HeadTableCell>Compromiso</HeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <RowTableCell colSpan={4} sx={{ textAlign: 'center', py: 3 }}>
                      Sin artículos
                    </RowTableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow key={row.uuid}>
                      <RowTableCell>{row.article_label}</RowTableCell>
                      <RowTableCell>
                        {formatSupplyOrderQuantity(row.requested_quantity)}
                      </RowTableCell>
                      <RowTableCell>
                        <CustomSelect
                          name="uuid_provider_legal_entity"
                          value={row.uuid_provider_legal_entity}
                          onChange={(event) =>
                            handleRowChange(
                              index,
                              'uuid_provider_legal_entity',
                              event.target.value
                            )
                          }
                          size="small"
                          sx={{ minWidth: 180 }}
                        >
                          {providers.map((provider) => (
                            <MenuItem key={provider.uuid} value={provider.uuid}>
                              {getProviderDisplayLabel(provider)}
                            </MenuItem>
                          ))}
                        </CustomSelect>
                      </RowTableCell>
                      <RowTableCell>
                        <CustomTextField
                          type="date"
                          value={row.provider_delivery_commitment_at}
                          onChange={(event) =>
                            handleRowChange(
                              index,
                              'provider_delivery_commitment_at',
                              event.target.value
                            )
                          }
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ minWidth: 150 }}
                        />
                      </RowTableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>
        </Box>
      </DialogBody>
      <DialogActions
        handleClose={onClose}
        handleSave={handleIssue}
        saveLabel="Emitir orden"
        saveButtonProps={{ isLoading: isProcessing }}
      />
    </DialogContainer>
  );
};

export default IssueSupplyOrderDialog;
